import {
  ITEM_DESCRIPTION_ALLOWED_TAGS,
  ITEM_DESCRIPTION_BLOCK_TAGS_TO_PARAGRAPH,
  isItemDescriptionLinkAllowed,
  isEmptyItemDescriptionHtml,
} from '@sharedlib/common/utils/item-description-html';
import DOMPurify, { type Config } from 'dompurify';

/**
 * The render-time half of the item description contract (`@sharedlib/common` `item-description-html`).
 * The backend sanitized the stored value on write with a different parser; sanitizing again here,
 * with the browser's, keeps a parser quirk on either side from becoming an XSS. DOMPurify is the
 * last step: everything else (block tags to paragraphs, unwrapping links) happens before it, on an
 * inert template, so no DOM is rebuilt from its output. Own instances: hooks and config are per
 * instance, and the comments' `safeHtml` pipe keeps its own.
 */
const CONFIG: Config = {
  ALLOWED_TAGS: [...ITEM_DESCRIPTION_ALLOWED_TAGS],
  ALLOWED_ATTR: ['href'],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
  KEEP_CONTENT: true,
};

const sanitizer = DOMPurify();
sanitizer.setConfig(CONFIG);

const renderer = DOMPurify();
renderer.setConfig(CONFIG);
// `target` / `rel` exist only in the rendered DOM — never stored, never sent in an invitation.
renderer.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const BLOCK_TAGS_SELECTOR = ITEM_DESCRIPTION_BLOCK_TAGS_TO_PARAGRAPH.join(',');

/**
 * What the sanitizer alone would not do: a link with a refused `href` keeps its text, and a dropped
 * block tag (a heading, a table cell, a `<div>`) becomes a paragraph rather than running its text
 * into the neighbours'. A `<template>` parses without running scripts or loading anything.
 */
function prepare(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  for (const anchor of Array.from(template.content.querySelectorAll('a'))) {
    if (!isItemDescriptionLinkAllowed(anchor.getAttribute('href'))) {
      anchor.replaceWith(...Array.from(anchor.childNodes));
    }
  }
  for (const block of Array.from(template.content.querySelectorAll(BLOCK_TAGS_SELECTOR))) {
    const paragraph = document.createElement('p');
    paragraph.append(...Array.from(block.childNodes));
    block.replaceWith(paragraph);
  }
  return template.innerHTML;
}

function finish(clean: string): string {
  const trimmed = clean.trim();
  return isEmptyItemDescriptionHtml(trimmed) ? '' : trimmed;
}

/** Sanitizes a stored description to the shared allow-list; nothing (`<p><br></p>`, whitespace) is `''`. */
export function sanitizeItemDescriptionHtml(html: string | null | undefined): string {
  if (!html) {
    return '';
  }
  return finish(sanitizer.sanitize(prepare(html)));
}

/** The description as it is shown: sanitized, with every link opening in a new tab. */
export function renderItemDescriptionHtml(html: string | null | undefined): string {
  if (!html) {
    return '';
  }
  return finish(renderer.sanitize(prepare(html)));
}
